import StarIcon from '@mui/icons-material/Star';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useCompanyDomains } from '@shared/hooks/useCompanyDomains';

interface CompanyDomainsPanelProps {
  companyId: number;
}

function CompanyDomainsPanel({ companyId }: CompanyDomainsPanelProps) {
  const { domains, loading, createDomain, deleteDomain, setPrimary } = useCompanyDomains(companyId);

  const [newDomain, setNewDomain] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [pendingId, setPendingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleAdd = async () => {
    const trimmed = newDomain.trim();
    if (!trimmed) {
      setError('Enter a domain');
      return;
    }
    setSubmitting(true);
    setError(null);
    const res = await createDomain({
      domain: trimmed,
      label: newLabel.trim() || null,
    });
    setSubmitting(false);
    if (res.error) {
      setError(res.message ?? 'Failed to add domain');
      return;
    }
    setNewDomain('');
    setNewLabel('');
  };

  const handleDelete = async (id: number) => {
    setPendingId(id);
    setError(null);
    const res = await deleteDomain(id);
    setPendingId(null);
    if (res.error) {
      setError(res.message ?? 'Failed to remove domain');
    }
  };

  const handleSetPrimary = async (id: number) => {
    setPendingId(id);
    setError(null);
    const res = await setPrimary(id);
    setPendingId(null);
    if (res.error) {
      setError(res.message ?? 'Failed to update primary domain');
    }
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        A company can own multiple domains (e.g. a parent brand and a product site). Domain matching for emails,
        calendar events, and contact discovery uses every domain listed here.
      </Typography>

      {error && <Box sx={{ color: 'error.main', fontSize: '0.875rem', mb: 1.5 }}>{error}</Box>}

      {loading && domains.length === 0 ? (
        <Stack alignItems="center" sx={{ py: 3 }}>
          <CircularProgress size={20} />
        </Stack>
      ) : (
        <List dense disablePadding>
          {domains.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
              No domains yet. Add the first one below — it'll be marked primary.
            </Typography>
          )}
          {domains.map((d) => (
            <ListItem
              key={d.id}
              disableGutters
              sx={{
                borderBottom: '1px solid',
                borderColor: 'divider',
                py: 1,
              }}
              secondaryAction={
                <Stack direction="row" spacing={0.5}>
                  <Tooltip title={d.isPrimary ? 'Primary' : 'Set as primary'}>
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => !d.isPrimary && handleSetPrimary(d.id)}
                        disabled={d.isPrimary || pendingId === d.id}
                        aria-label={d.isPrimary ? 'Primary domain' : 'Set as primary'}
                      >
                        {d.isPrimary ? (
                          <StarIcon fontSize="small" color="warning" />
                        ) : (
                          <StarOutlineIcon fontSize="small" />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={d.isPrimary ? 'Promote another domain first' : 'Remove'}>
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(d.id)}
                        disabled={d.isPrimary || pendingId === d.id}
                        aria-label="Remove domain"
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              }
            >
              <Box sx={{ pr: 10 }}>
                <Typography variant="body2" sx={{ fontWeight: d.isPrimary ? 600 : 400 }}>
                  {d.domain}
                </Typography>
                {d.label && (
                  <Typography variant="caption" color="text.secondary">
                    {d.label}
                  </Typography>
                )}
              </Box>
            </ListItem>
          ))}
        </List>
      )}

      <Stack direction="row" spacing={1} sx={{ mt: 3 }}>
        <TextField
          size="small"
          label="Domain"
          placeholder="example.com"
          value={newDomain}
          onChange={(e) => setNewDomain(e.target.value)}
          disabled={submitting}
          fullWidth
        />
        <TextField
          size="small"
          label="Label (optional)"
          placeholder="product"
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          disabled={submitting}
          sx={{ minWidth: 160 }}
        />
        <Button
          variant="contained"
          onClick={handleAdd}
          disabled={submitting || newDomain.trim().length === 0}
          startIcon={submitting ? <CircularProgress size={14} /> : undefined}
        >
          Add
        </Button>
      </Stack>
    </Box>
  );
}

export default CompanyDomainsPanel;
